import type { ReactNode } from 'react';
import { useId } from 'react';

import classNames from 'classnames';

import styles from './StatusMessage.module.css';

import { visuallyHidden as visuallyHiddenStyle } from '../../styles';
import { ContentReveal } from '../ContentReveal';

export type PolitenessSetting = 'off' | 'assertive' | 'polite';

export interface StatusMessageProps {
  id?: string;
  /** Toggle the invalid design. */
  invalid?: boolean;
  /** Visually hide the message, while keeping it in the accessibility tree. */
  visuallyHidden?: boolean;
  /** Politeness setting used for aria-live regions. */
  politeness: PolitenessSetting;
  /** The message content. */
  children?: ReactNode;
  className?: string;
}

/**
 * Render a WCAG compliant status message whose content is dynamic.
 *
 * @see https://corgi-x.tfd.engineering/components/statusmessage
 */
export function StatusMessage(props: StatusMessageProps) {
  const {
    invalid,
    visuallyHidden = false,
    className,
    children,
    politeness,
    ...divProps
  } = props;
  const reactId = useId();
  const id = props.id || reactId;

  const childClassName = classNames(className, {
    [visuallyHiddenStyle]: visuallyHidden,
    [styles.invalid]: invalid,
  });
  return (
    <ContentReveal inAnimation="fade" outAnimation="fade" in={!visuallyHidden}>
      <div
        {...divProps}
        id={id}
        role="status"
        aria-live={politeness}
        className={childClassName}
      >
        {children}
      </div>
    </ContentReveal>
  );
}
